import { capitalize, cloneDeep, mean, orderBy, set } from 'lodash'
import { RATING_TYPES } from '../common/utils'

export const packagerPrefix = 'EA-'
export const analysisEditingUrl = 'edit analysis'
export const analysisTop = 270

export function getReturnPath(urlParams: string[]): string {
    const paramsArray = [...urlParams]
    paramsArray.pop()

    return paramsArray.join('/')
}

export function getFinalScore(scores: number[]): number {
    const invalidValues = scores.filter(score => score === undefined || score === null)
    if (invalidValues.length) return -1

    const scoresAsPercentages = (scores: number[]): number[] => scores.map(score => 100 - score * 25)
    const percentages = scoresAsPercentages(scores)
    const percentagesUnder50 = percentages.filter(percentage => percentage <= 50)

    if (percentagesUnder50.length) return Math.min(...percentagesUnder50)
    else return Math.round(mean(percentages))
}

/**
 *
 * Tests if the weight is less than the reference grammage
 */
export const isWeightCritical = (weight: number, grammage: number): boolean => weight < getMinWeight(grammage)

export function getMinWeight(grammage: number): number {
    if (grammage < 50) return grammage * 0.91
    else if (grammage < 100) return grammage - 4.5
    else if (grammage < 200) return grammage * 0.955
    else if (grammage < 300) return grammage - 9
    else if (grammage < 501) return grammage * 0.97
    else if (grammage < 1001) return grammage - 15
    else return -1
}

/**
 * If there is no critical weights, it returns the mean of all weights,
 * if there is one or more criticals, it returns the lowest
 */
export function getReducedWeighings(weighings: IWeighing[]): { weights: number[]; critical: number; delta: number } {
    return weighings.reduce(
        (acc, weighing) => {
            const { weight, grammage } = weighing
            if (!weight || !grammage || weight > grammage + grammage * 0.1) return acc

            const weightDelta = Math.abs(grammage - weight) / grammage
            if (isWeightCritical(weight, grammage) && weightDelta > acc.delta) {
                acc.critical = weight
                acc.delta = weightDelta
                acc.weights.push(weight)

                return acc
            } else if (weight) {
                acc.weights.push(weight)

                return acc
            }
        },
        { weights: [], critical: 0, delta: 0 }
    )
}
export function getWeighingMeanValue(reducedWeighings: { weights: number[]; critical: number; delta: number }): number {
    if (reducedWeighings.critical) {
        return reducedWeighings.critical
    } else if (reducedWeighings.weights.length) {
        const numberArray = reducedWeighings.weights.map(Number)
        const meanValue = mean(numberArray)

        return Number(meanValue.toFixed(2))
    } else {
        return null
    }
}

export const initialGeneralInfo = (): GeneralInfo => {
    return {
        participants: [null, null, null],
        weighings: [getEmptyWeighing(), getEmptyWeighing()],
        weighingAverage: 0,
        packagers: [],
        product: '',
        batch: '',
        reference: false,
        weighingScore: undefined,
        sku: '',
        country: '',
        factory: '',
        analysisType: 'icc',
        line: ''
    }
}

export const getEmptyWeighing = (): IWeighing => ({ packager: packagerPrefix, grammage: '', weight: '' })

export const initialQualityRating = (): RatingInfo => {
    return {
        packaging: { score: null, deviations: { allDeviations: new Set() } },
        appearance: { score: null, deviations: { allDeviations: new Set() } },
        texture: { score: null, deviations: { allDeviations: new Set() } },
        taste: { score: null, deviations: { allDeviations: new Set() } }
    }
}

export function getSkuSelectOptionsFromAssetTree(
    assetTree: CountryData[],
    country: string,
    factory: string,
    line: string,
    product?: string
): SkuSelectOptions[] {
    const countryObj = assetTree.filter(countryData => countryData.countryName === country)?.[0]
    const factoryObj = countryObj?.factories.filter(factoryData => factoryData.factoryName === factory)?.[0]
    const lineObj = factoryObj?.lines.filter(lineData => lineData.lineName === line)?.[0]

    if (!countryObj || !factoryObj || !lineObj) return []

    let orderedSkus: { skuName: string; skuProduct: string }[] = []
    if (product) {
        const productObj = lineObj.products.filter(productData => productData.productName === product)?.[0]
        orderedSkus = orderBy(productObj.skus, [sku => sku.toLowerCase()]).map(sku => ({
            skuName: sku,
            skuProduct: product
        }))
    } else {
        const skusArray = lineObj.products.reduce((acc, product) => {
            product.skus.forEach(sku => acc.push({ skuName: sku, skuProduct: product.productName }))

            return acc
        }, [])
        orderedSkus = orderBy(skusArray, [sku => sku.skuName.toLowerCase()])
    }
    const skuInSelectFormat = orderedSkus.map(sku => ({
        value: sku.skuName,
        label: sku.skuName.toUpperCase(),
        product: product || sku.skuProduct
    }))

    return skuInSelectFormat
}

export function getEmptyActionPlan(): ActionPlan {
    return {
        whatHappened: '',
        whatShouldBeDone: '',
        whenItWillBeDone: 0,
        whoDidIt: '',
        planResults: '',
        planStatus: false
    }
}

export function convertRatingSetsIntoArrays(ratingInfo: RatingInfo, packagers: string[]): JSONConvertedRatingInfo {
    let convertedRatingInfo = {}
    convertedRatingInfo = cloneDeep(ratingInfo)
    const ratingTypes: Array<keyof RatingInfo> = ['appearance', 'texture', 'taste', 'packaging']

    ratingTypes.forEach(ratingType => {
        set(convertedRatingInfo, `${ratingType}.deviations.allDeviations`, [
            ...ratingInfo[ratingType].deviations.allDeviations
        ])
    })

    packagers.forEach(packager => {
        const packagerInfo = ratingInfo.packaging.deviations[packager]
        if (packagerInfo) {
            set(convertedRatingInfo, `packaging.deviations[${packager}]`, [...packagerInfo])
        }
    })

    return convertedRatingInfo as JSONConvertedRatingInfo
}

export const plansHaveEmptyFields = (actionPlans: ActionPlan[]): boolean =>
    actionPlans.reduce((acc: boolean, plan) => {
        if (!acc) {
            const invalidFields = getActionPlanInvalidFields(plan)
            if (invalidFields.length) acc = true

            return acc
        }
    }, false)

export function getActionPlanInvalidFields(actionPlan: ActionPlan): string[] {
    const { whatHappened, whatShouldBeDone, whenItWillBeDone, whoDidIt, planResults, planStatus } = actionPlan

    const pendingRequired = { whatHappened, whatShouldBeDone }
    const doneRequired = { ...pendingRequired, whenItWillBeDone, whoDidIt, planResults }
    if (planStatus) {
        return Object.keys(doneRequired).filter(key => !actionPlan[key])
    } else {
        return Object.keys(pendingRequired).filter(key => !actionPlan[key])
    }
}

export function getActiveRatings(urlParams: string[], assetTree: CountryData[]): typeof RATING_TYPES[number][] {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [paramsTab, paramsCountry, paramsFactory, paramsLine] = urlParams
    const selectedCountry = assetTree.filter(country => country.countryName === paramsCountry)[0]
    const selectedFactory = selectedCountry?.factories.filter(factory => factory.factoryName === paramsFactory)[0]
    const selectedLine = selectedFactory?.lines.filter(line => line.lineName === paramsLine)[0]

    return RATING_TYPES.filter(type => selectedLine?.activeAnalysis[type])
}

export function getDeviations(urlParams: string[], assetTree: CountryData[]): Deviations {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [paramsTab, paramsCountry, paramsFactory, paramsLine] = urlParams
    const selectedCountry = assetTree.filter(country => country.countryName === paramsCountry)[0]
    const selectedFactory = selectedCountry?.factories.filter(factory => factory.factoryName === paramsFactory)[0]
    const selectedLine = selectedFactory?.lines.filter(line => line.lineName === paramsLine)[0]
    if (selectedLine.deviations) {
        return selectedLine.deviations
    }

    return DEVIATIONS
}

const DEVIATIONS = {
    packaging: [
        'aparência da solda horizontal',
        'aparência da solda vertical',
        'bolsa mordida',
        'centralização horizontal',
        'código falho',
        'corte horizontal',
        'defeitos de impressão',
        'delaminação',
        'dobra',
        'emendas',
        'estrias',
        'fita promocional desalinhada',
        'fora de passo',
        'macrofuro',
        'marcas de correia',
        'posição do código',
        'rompimento',
        'selagem fraca',
        'selagem vertical descentralizada',
        'strip paper'
    ],
    appearance: [
        'aparência oleosa',
        'bolhas',
        'coloração (claro)',
        'coloração (escuro)',
        'coloração (variação)',
        'comprimento',
        'cor indesejável',
        'curvados',
        'dano por inseto',
        'dano seco',
        'defeitos',
        'defeitos de batata',
        'diâmetro',
        'dobrados',
        'espessura',
        'excesso de aroma',
        'excesso de pele',
        'expansão',
        'flat',
        'flocos escuros',
        'formato',
        'grudados',
        'largura',
        'oleoso',
        'pele',
        'polpa amarela',
        'pontos brancos',
        'pontos duros',
        'pontos tostados',
        'poroso',
        'proporção do mix',
        'quebrados',
        'queimado',
        'rebarbas',
        'recobrimento',
        'resíduo de forno',
        'rugoso',
        'scraps',
        'variação collet',
        'variedade do milho',
        'verdes'
    ],
    texture: [
        'aerado',
        'cru',
        'difícil deglutição',
        'duro / vítrio',
        'gritz de milho',
        'murcho',
        'pontos duros',
        'poroso',
        'seco',
        'soft',
        'variação de textura'
    ],
    taste: [
        'amargo',
        'amendoim',
        'azedo',
        'residual de base',
        'sabor alterado',
        'sabor de óleo',
        'sabor de queimado',
        'sal alto',
        'sal baixo',
        'variação de sabor',
        'variação de sal'
    ]
}
